'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @primitive DateLabel
 * ROLE: time
 * ARIA: dateTime carries ISO-8601; title carries absolute formatted date
 * KEYBOARD: standard (not focusable)
 * FOCUS: not focusable
 * LIVE-REGION: none — relative-time ticks are silent (text re-renders, no announcement)
 * PRM: no animation
 * FORCED-COLORS: standard
 * RTL: text follows surrounding direction
 * RSC: CLIENT (re-render scheduling requires setInterval)
 *
 * The `format` prop is consumer-injected. DS does not import next-intl.
 * Wire the formatter from next-intl's `useFormatter()` at the call site.
 *
 * Refresh interval is derived from the elapsed age at mount:
 *   < 1 h  → refresh every 60 s (minute-granularity relative time)
 *   < 24 h → refresh every 5 min (hour-granularity relative time)
 *   ≥ 24 h → refresh every 1 h (day-granularity relative time)
 */
import { useEffect, useRef, useState } from 'react';
function toDate(v) {
    return v instanceof Date ? v : new Date(v);
}
function toIso(v) {
    return v instanceof Date ? v.toISOString() : v;
}
/** Pick a refresh interval (ms) based on how old the date is. */
function refreshInterval(date) {
    const ageMs = Date.now() - date.getTime();
    if (ageMs < 60 * 60 * 1000)
        return 60_000; // < 1 h  → every 60 s
    if (ageMs < 24 * 60 * 60 * 1000)
        return 5 * 60_000; // < 24 h → every 5 min
    return 60 * 60_000; // ≥ 24 h → every 1 h
}
export function DateLabel({ value, format, className }) {
    const date = toDate(value);
    const iso = toIso(value);
    const [, tick] = useState(0);
    const intervalRef = useRef(null);
    useEffect(() => {
        function schedule() {
            intervalRef.current = setInterval(() => {
                tick((n) => n + 1);
                // Re-derive interval on each tick so granularity coarsens as the date ages.
                if (intervalRef.current !== null)
                    clearInterval(intervalRef.current);
                schedule();
            }, refreshInterval(date));
        }
        schedule();
        return () => {
            if (intervalRef.current !== null)
                clearInterval(intervalRef.current);
        };
        // date reference is stable for a given `value` string; re-run only if value changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iso]);
    // Absolute title for hover / AT; displayed text is the formatted relative value.
    const absolute = date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
    return (_jsx("time", { dateTime: iso, title: absolute, className: className, children: format(date) }));
}
//# sourceMappingURL=DateLabel.js.map